import React, { memo, useState, useMemo } from "react";
import {
  Box,
  Container,
  Grid2,
  Typography,
  Card,
  CardActionArea,
  Skeleton,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Search, LocationOn, ArrowForward, Clear } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";

// All 50 US States for the filter dropdown
const ALL_US_STATES = [
  { state: "AL", stateName: "Alabama" },
  { state: "AK", stateName: "Alaska" },
  { state: "AZ", stateName: "Arizona" },
  { state: "AR", stateName: "Arkansas" },
  { state: "CA", stateName: "California" },
  { state: "CO", stateName: "Colorado" },
  { state: "CT", stateName: "Connecticut" },
  { state: "DE", stateName: "Delaware" },
  { state: "FL", stateName: "Florida" },
  { state: "GA", stateName: "Georgia" },
  { state: "HI", stateName: "Hawaii" },
  { state: "ID", stateName: "Idaho" },
  { state: "IL", stateName: "Illinois" },
  { state: "IN", stateName: "Indiana" },
  { state: "IA", stateName: "Iowa" },
  { state: "KS", stateName: "Kansas" },
  { state: "KY", stateName: "Kentucky" },
  { state: "LA", stateName: "Louisiana" },
  { state: "ME", stateName: "Maine" },
  { state: "MD", stateName: "Maryland" },
  { state: "MA", stateName: "Massachusetts" },
  { state: "MI", stateName: "Michigan" },
  { state: "MN", stateName: "Minnesota" },
  { state: "MS", stateName: "Mississippi" },
  { state: "MO", stateName: "Missouri" },
  { state: "MT", stateName: "Montana" },
  { state: "NE", stateName: "Nebraska" },
  { state: "NV", stateName: "Nevada" },
  { state: "NH", stateName: "New Hampshire" },
  { state: "NJ", stateName: "New Jersey" },
  { state: "NM", stateName: "New Mexico" },
  { state: "NY", stateName: "New York" },
  { state: "NC", stateName: "North Carolina" },
  { state: "ND", stateName: "North Dakota" },
  { state: "OH", stateName: "Ohio" },
  { state: "OK", stateName: "Oklahoma" },
  { state: "OR", stateName: "Oregon" },
  { state: "PA", stateName: "Pennsylvania" },
  { state: "RI", stateName: "Rhode Island" },
  { state: "SC", stateName: "South Carolina" },
  { state: "SD", stateName: "South Dakota" },
  { state: "TN", stateName: "Tennessee" },
  { state: "TX", stateName: "Texas" },
  { state: "UT", stateName: "Utah" },
  { state: "VT", stateName: "Vermont" },
  { state: "VA", stateName: "Virginia" },
  { state: "WA", stateName: "Washington" },
  { state: "WV", stateName: "West Virginia" },
  { state: "WI", stateName: "Wisconsin" },
  { state: "WY", stateName: "Wyoming" },
];

// Popular US cities (major cities from each state)
const POPULAR_CITIES = [
  // Major metros
  { city: "New York", state: "NY", stateName: "New York" },
  { city: "Los Angeles", state: "CA", stateName: "California" },
  { city: "Chicago", state: "IL", stateName: "Illinois" },
  { city: "Houston", state: "TX", stateName: "Texas" },
  { city: "Phoenix", state: "AZ", stateName: "Arizona" },
  { city: "Philadelphia", state: "PA", stateName: "Pennsylvania" },
  { city: "San Antonio", state: "TX", stateName: "Texas" },
  { city: "San Diego", state: "CA", stateName: "California" },
  { city: "Dallas", state: "TX", stateName: "Texas" },
  { city: "San Jose", state: "CA", stateName: "California" },
  { city: "Austin", state: "TX", stateName: "Texas" },
  { city: "Jacksonville", state: "FL", stateName: "Florida" },
  { city: "Fort Worth", state: "TX", stateName: "Texas" },
  { city: "Columbus", state: "OH", stateName: "Ohio" },
  { city: "Charlotte", state: "NC", stateName: "North Carolina" },
  { city: "San Francisco", state: "CA", stateName: "California" },
  { city: "Indianapolis", state: "IN", stateName: "Indiana" },
  { city: "Seattle", state: "WA", stateName: "Washington" },
  { city: "Denver", state: "CO", stateName: "Colorado" },
  { city: "Washington", state: "DC", stateName: "District of Columbia" },
  { city: "Boston", state: "MA", stateName: "Massachusetts" },
  { city: "El Paso", state: "TX", stateName: "Texas" },
  { city: "Nashville", state: "TN", stateName: "Tennessee" },
  { city: "Detroit", state: "MI", stateName: "Michigan" },
  { city: "Oklahoma City", state: "OK", stateName: "Oklahoma" },
  { city: "Portland", state: "OR", stateName: "Oregon" },
  { city: "Las Vegas", state: "NV", stateName: "Nevada" },
  { city: "Memphis", state: "TN", stateName: "Tennessee" },
  { city: "Louisville", state: "KY", stateName: "Kentucky" },
  { city: "Baltimore", state: "MD", stateName: "Maryland" },
  { city: "Milwaukee", state: "WI", stateName: "Wisconsin" },
  { city: "Albuquerque", state: "NM", stateName: "New Mexico" },
  { city: "Tucson", state: "AZ", stateName: "Arizona" },
  { city: "Fresno", state: "CA", stateName: "California" },
  { city: "Sacramento", state: "CA", stateName: "California" },
  { city: "Mesa", state: "AZ", stateName: "Arizona" },
  { city: "Kansas City", state: "MO", stateName: "Missouri" },
  { city: "Atlanta", state: "GA", stateName: "Georgia" },
  { city: "Miami", state: "FL", stateName: "Florida" },
  { city: "Colorado Springs", state: "CO", stateName: "Colorado" },
  { city: "Raleigh", state: "NC", stateName: "North Carolina" },
  { city: "Omaha", state: "NE", stateName: "Nebraska" },
  { city: "Long Beach", state: "CA", stateName: "California" },
  { city: "Virginia Beach", state: "VA", stateName: "Virginia" },
  { city: "Oakland", state: "CA", stateName: "California" },
  { city: "Minneapolis", state: "MN", stateName: "Minnesota" },
  { city: "Tulsa", state: "OK", stateName: "Oklahoma" },
  { city: "Tampa", state: "FL", stateName: "Florida" },
  { city: "Arlington", state: "TX", stateName: "Texas" },
  { city: "New Orleans", state: "LA", stateName: "Louisiana" },
  // More cities to cover all states
  { city: "Birmingham", state: "AL", stateName: "Alabama" },
  { city: "Anchorage", state: "AK", stateName: "Alaska" },
  { city: "Little Rock", state: "AR", stateName: "Arkansas" },
  { city: "Bridgeport", state: "CT", stateName: "Connecticut" },
  { city: "Wilmington", state: "DE", stateName: "Delaware" },
  { city: "Honolulu", state: "HI", stateName: "Hawaii" },
  { city: "Boise", state: "ID", stateName: "Idaho" },
  { city: "Des Moines", state: "IA", stateName: "Iowa" },
  { city: "Wichita", state: "KS", stateName: "Kansas" },
  { city: "Portland", state: "ME", stateName: "Maine" },
  { city: "Jackson", state: "MS", stateName: "Mississippi" },
  { city: "Billings", state: "MT", stateName: "Montana" },
  { city: "Manchester", state: "NH", stateName: "New Hampshire" },
  { city: "Newark", state: "NJ", stateName: "New Jersey" },
  { city: "Buffalo", state: "NY", stateName: "New York" },
  { city: "Fargo", state: "ND", stateName: "North Dakota" },
  { city: "Providence", state: "RI", stateName: "Rhode Island" },
  { city: "Charleston", state: "SC", stateName: "South Carolina" },
  { city: "Sioux Falls", state: "SD", stateName: "South Dakota" },
  { city: "Salt Lake City", state: "UT", stateName: "Utah" },
  { city: "Burlington", state: "VT", stateName: "Vermont" },
  { city: "Richmond", state: "VA", stateName: "Virginia" },
  { city: "Charleston", state: "WV", stateName: "West Virginia" },
  { city: "Cheyenne", state: "WY", stateName: "Wyoming" },
  // Additional popular cities
  { city: "Orlando", state: "FL", stateName: "Florida" },
  { city: "Cleveland", state: "OH", stateName: "Ohio" },
  { city: "Pittsburgh", state: "PA", stateName: "Pennsylvania" },
  { city: "Cincinnati", state: "OH", stateName: "Ohio" },
  { city: "St. Louis", state: "MO", stateName: "Missouri" },
  { city: "Scottsdale", state: "AZ", stateName: "Arizona" },
  { city: "Irvine", state: "CA", stateName: "California" },
  { city: "Plano", state: "TX", stateName: "Texas" },
  { city: "Henderson", state: "NV", stateName: "Nevada" },
  { city: "Savannah", state: "GA", stateName: "Georgia" },
  { city: "Baton Rouge", state: "LA", stateName: "Louisiana" },
  { city: "Mobile", state: "AL", stateName: "Alabama" },
  { city: "Spokane", state: "WA", stateName: "Washington" },
  { city: "Madison", state: "WI", stateName: "Wisconsin" },
  { city: "Lexington", state: "KY", stateName: "Kentucky" },
  { city: "Durham", state: "NC", stateName: "North Carolina" },
  { city: "Knoxville", state: "TN", stateName: "Tennessee" },
  { city: "Santa Fe", state: "NM", stateName: "New Mexico" },
  { city: "Reno", state: "NV", stateName: "Nevada" },
  { city: "Rochester", state: "NY", stateName: "New York" },
  { city: "Bakersfield", state: "CA", stateName: "California" },
  { city: "Aurora", state: "CO", stateName: "Colorado" },
  { city: "Anaheim", state: "CA", stateName: "California" },
  { city: "Santa Ana", state: "CA", stateName: "California" },
  { city: "St. Petersburg", state: "FL", stateName: "Florida" },
  { city: "Chandler", state: "AZ", stateName: "Arizona" },
];

// City card gradient colors
const getCityGradient = (index: number) => {
  const gradients = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
    "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
    "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)",
  ];
  return gradients[index % gradients.length];
};

const CityCard = memo(({ cityData, index }: { cityData: any; index: number }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/${cityData.state}/city/${cityData.city}`);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        transition: "all 0.3s ease",
        border: "1px solid #e0e0e0",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          "& .city-arrow": {
            transform: "translateX(4px)",
            opacity: 1,
          },
        },
      }}
    >
      <CardActionArea onClick={handleClick}>
        <Box
          sx={{
            background: getCityGradient(index),
            height: { xs: 80, sm: 100 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "rgba(255,255,255,0.95)",
              fontWeight: 700,
              textAlign: "center",
              px: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {cityData.city}
          </Typography>
        </Box>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LocationOn
                sx={{ fontSize: 16, color: "primary.main", opacity: 0.8 }}
              />
              <Typography
                variant="body2"
                fontWeight={500}
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {cityData.stateName}
              </Typography>
            </Box>
            <ArrowForward
              className="city-arrow"
              sx={{
                fontSize: 16,
                color: "primary.main",
                opacity: 0.5,
                transition: "all 0.3s ease",
              }}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            Browse real estate
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
});

const CityCardSkeleton = () => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "16px",
      overflow: "hidden",
      border: "1px solid #e0e0e0",
    }}
  >
    <Skeleton variant="rectangular" height={100} animation="wave" />
    <Box sx={{ p: 2 }}>
      <Skeleton variant="text" width="60%" height={20} animation="wave" />
      <Skeleton variant="text" width="40%" height={16} animation="wave" />
    </Box>
  </Card>
);

const CityList = memo(() => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [isLoading] = useState(false);

  // Filter cities based on search query and selected state
  const filteredCities = useMemo(() => {
    return POPULAR_CITIES.filter((cityData) => {
      const matchesSearch =
        !searchQuery ||
        cityData.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cityData.stateName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesState = !selectedState || cityData.state === selectedState;

      return matchesSearch && matchesState;
    });
  }, [searchQuery, selectedState]);

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh" }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
            py: { xs: 6, md: 10 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              top: -100,
              right: -50,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              bottom: -50,
              left: "20%",
            }}
          />

          <Container maxWidth="xl">
            <FullPaddingWrapper>
              <Box textAlign="center" position="relative" zIndex={1}>
                <Typography
                  variant="h2"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  }}
                >
                  Browse by City
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontWeight: 400,
                    mb: 4,
                    maxWidth: 600,
                    mx: "auto",
                    fontSize: { xs: "1rem", md: "1.1rem" },
                  }}
                >
                  Explore real estate listings in major cities across the United
                  States. Search or filter by state to find your perfect location.
                </Typography>

                {/* Search Box + State Filter */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ maxWidth: 700, mx: "auto" }}
                  alignItems="center"
                >
                  <TextField
                    fullWidth
                    placeholder="Search for a city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flex: 2,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "12px",
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                  <FormControl
                    sx={{
                      flex: 1,
                      minWidth: { xs: "100%", sm: 200 },
                    }}
                  >
                    <Select
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "transparent",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      <MenuItem value="">All States</MenuItem>
                      {ALL_US_STATES.map((state) => (
                        <MenuItem key={state.state} value={state.state}>
                          {state.stateName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {/* Clear Filter Button */}
                  {(searchQuery || selectedState) && (
                    <Tooltip title="Clear filters">
                      <IconButton
                        onClick={() => {
                          setSearchQuery("");
                          setSelectedState("");
                        }}
                        sx={{
                          backgroundColor: "white",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.9)",
                          },
                          width: 48,
                          height: 48,
                        }}
                      >
                        <Clear />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            </FullPaddingWrapper>
          </Container>
        </Box>

        {/* Cities Grid */}
        <Container maxWidth="xl">
          <FullPaddingWrapper>
            <Box py={{ xs: 4, md: 8 }}>
              {/* Results count */}
              {!isLoading && (
                <Box mb={4}>
                  <Typography variant="body1" color="text.secondary">
                    {searchQuery || selectedState
                      ? `${filteredCities.length} cities found`
                      : `${POPULAR_CITIES.length} popular cities`}
                  </Typography>
                </Box>
              )}

              <Grid2 container spacing={{ xs: 2, sm: 3 }}>
                {isLoading
                  ? Array.from(new Array(12)).map((_, index) => (
                      <Grid2
                        key={index}
                        size={{
                          xs: 6,
                          sm: 4,
                          md: 3,
                          lg: 12 / 5,
                        }}
                      >
                        <CityCardSkeleton />
                      </Grid2>
                    ))
                  : filteredCities.map((cityData, index) => (
                      <Grid2
                        key={`${cityData.city}-${cityData.state}-${index}`}
                        size={{
                          xs: 6,
                          sm: 4,
                          md: 3,
                          lg: 12 / 5,
                        }}
                      >
                        <CityCard cityData={cityData} index={index} />
                      </Grid2>
                    ))}
              </Grid2>

              {/* No results message */}
              {!isLoading && filteredCities.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 10,
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No cities found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search or state filter
                  </Typography>
                </Box>
              )}
            </Box>
          </FullPaddingWrapper>
        </Container>
      </Box>
    </Box>
  );
});

export default CityList;
